/**
 * MissingValueIndicator Component
 *
 * Purpose: Visual indicator when an MDM value UUID cannot be resolved
 * Architecture: Small inline component with error icon and tooltip
 *
 * Usage: Displayed in MDTable cells when an MDM UUID cannot be looked up
 *
 * Author: Development Team
 * Created: 2025-11-30
 */

import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { ErrorOutline as ErrorIcon } from '@mui/icons-material';

interface MissingValueIndicatorProps {
  /** The UUID that could not be resolved */
  uuid: string;
  /** Optional custom message */
  message?: string;
  /** Show the full UUID or truncated (default: truncated) */
  showFullUuid?: boolean;
}

/**
 * MissingValueIndicator
 *
 * Purpose: Shows a red exclamation icon with tooltip when an MDM value cannot be resolved
 * Logic: Displays truncated UUID in tooltip for debugging, error styling for visibility
 *
 * @param uuid - The UUID that could not be resolved
 * @param message - Optional custom tooltip message
 * @param showFullUuid - Whether to show the full UUID (default: false, shows first 8 chars)
 */
const MissingValueIndicator: React.FC<MissingValueIndicatorProps> = ({
  uuid,
  message,
  showFullUuid = false,
}) => {
  const truncatedUuid = uuid && uuid.length > 8 ? `${uuid.substring(0, 8)}...` : uuid;
  const displayUuid = showFullUuid ? uuid : truncatedUuid;
  const tooltipMessage = message || `Value not found: ${displayUuid}`;

  return (
    <Tooltip title={tooltipMessage} arrow>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          color: 'error.main',
          fontSize: '0.875rem',
          cursor: 'help',
        }}
        aria-label={tooltipMessage}
      >
        <ErrorIcon
          sx={{
            fontSize: '1rem',
            mr: 0.5,
          }}
        />
        <span style={{ fontStyle: 'italic', opacity: 0.8 }}>Missing</span>
      </Box>
    </Tooltip>
  );
};

export default MissingValueIndicator;
